
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function PortfolioPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const projects = [
    {
      titleKey: 'portfolioPage.energy.title',
      descKey: 'portfolioPage.energy.desc',
      fullDescKey: 'portfolioPage.energy.fullDesc',
      icon: 'ri-lightbulb-line',
      image: 'https://readdy.ai/api/search-image?query=advanced%20battery%20energy%20storage%20system%20with%20modern%20BMS%20technology%2C%20electric%20vehicle%20battery%20pack%20manufacturing%20facility%2C%20industrial%20power%20solutions%2C%20clean%20energy%20technology%2C%20professional%20product%20photography%20with%20warm%20golden%20lighting%2C%20innovation%20in%20energy%20sector%2C%20premium%20quality&width=800&height=600&seq=alebel-portfolio-energy-001&orientation=landscape',
      tags: ['portfolioPage.tags.energy', 'portfolioPage.tags.batterySystems', 'portfolioPage.tags.industrial', 'portfolioPage.tags.electricMobility'],
      color: 'from-amber-500 to-orange-600',
    },
    {
      titleKey: 'portfolioPage.electronics.title',
      descKey: 'portfolioPage.electronics.desc',
      fullDescKey: 'portfolioPage.electronics.fullDesc',
      icon: 'ri-cpu-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20electronics%20circuit%20board%20with%20IoT%20devices%20and%20embedded%20systems%2C%20RF%20communication%20modules%2C%20smart%20control%20systems%20technology%2C%20professional%20tech%20photography%20with%20warm%20lighting%2C%20innovation%20in%20electronics%20industry%2C%20premium%20quality%20components&width=800&height=600&seq=alebel-portfolio-electronics-001&orientation=landscape',
      tags: ['portfolioPage.tags.electronics', 'portfolioPage.tags.iot', 'portfolioPage.tags.smartSystems', 'portfolioPage.tags.rfCommunication'],
      color: 'from-cyan-500 to-teal-600',
    },
    {
      titleKey: 'portfolioPage.construction.title',
      descKey: 'portfolioPage.construction.desc',
      fullDescKey: 'portfolioPage.construction.fullDesc',
      icon: 'ri-building-2-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20construction%20site%20with%20infrastructure%20development%20in%20UAE%2C%20contemporary%20building%20project%20with%20cranes%20and%20modern%20architecture%2C%20professional%20construction%20management%2C%20turnkey%20building%20solutions%2C%20architectural%20excellence%2C%20golden%20hour%20photography%2C%20construction%20industry&width=800&height=600&seq=alebel-portfolio-construction-001&orientation=landscape',
      tags: ['portfolioPage.tags.construction', 'portfolioPage.tags.infrastructure', 'portfolioPage.tags.development', 'portfolioPage.tags.turnkeyProjects'],
      color: 'from-emerald-500 to-green-600',
    },
    {
      titleKey: 'portfolioPage.rd.title',
      descKey: 'portfolioPage.rd.desc',
      fullDescKey: 'portfolioPage.rd.fullDesc',
      icon: 'ri-flask-line',
      image: '/alebelrnd.png',
      tags: ['portfolioPage.tags.research', 'portfolioPage.tags.innovation', 'portfolioPage.tags.technology', 'portfolioPage.tags.development'],
      color: 'from-purple-500 to-indigo-600',
    },
    {
      titleKey: 'portfolioPage.robotics.title',
      descKey: 'portfolioPage.robotics.desc',
      fullDescKey: 'portfolioPage.robotics.fullDesc',
      icon: 'ri-robot-line',
      image: '/alebelrobotics.png',
      tags: ['portfolioPage.tags.robotics', 'portfolioPage.tags.automation', 'portfolioPage.tags.ai', 'portfolioPage.tags.industrial'],
      color: 'from-blue-500 to-cyan-600',
    },
    {
      titleKey: 'portfolioPage.device.title',
      descKey: 'portfolioPage.device.desc',
      fullDescKey: 'portfolioPage.device.fullDesc',
      icon: 'ri-remote-control-line',
      image: '/alebeldevice.png',
      tags: ['portfolioPage.tags.devices', 'portfolioPage.tags.smartTech', 'portfolioPage.tags.electronics', 'portfolioPage.tags.iot'],
      color: 'from-rose-500 to-pink-600',
    },
  ];

  const sectors = [
    { nameKey: 'portfolioPage.sectors.energy', icon: 'ri-flashlight-line' },
    { nameKey: 'portfolioPage.sectors.technology', icon: 'ri-code-s-slash-line' },
    { nameKey: 'portfolioPage.sectors.manufacturing', icon: 'ri-settings-3-line' },
    { nameKey: 'portfolioPage.sectors.electronics', icon: 'ri-cpu-line' },
    { nameKey: 'portfolioPage.sectors.industrial', icon: 'ri-building-4-line' },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      {/* Hero Section with Animated Gradient */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-[#1A3A5C] to-[#0D2137] animate-gradient"></div>
        
        {/* Moving Color Overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/10 animate-gradient"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/5 to-transparent animate-gradient" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(201, 169, 98, 0.2) 0%, transparent 50%)`,
          }}></div>
        </div>
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className={`flex items-center gap-2 text-sm mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/" className="text-gray-400 hover:text-gold transition-colors cursor-pointer">{t('portfolioPage.breadcrumb.home')}</Link>
            <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-gray-500`}></i>
            <span className="text-gold">{t('portfolioPage.breadcrumb.portfolio')}</span>
          </div>
          
          <div className="max-w-3xl">
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('portfolioPage.hero.label')}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl text-white leading-tight mb-6">
              {t('portfolioPage.hero.title1')} <span className="text-gold">{t('portfolioPage.hero.title2')}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
              {t('portfolioPage.hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="space-y-16 md:space-y-24">
            {projects.map((project, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center`}>
                <div className={`relative ${index % 2 === 1 ? (isRTL ? 'lg:order-1' : 'lg:order-2') : (isRTL ? 'lg:order-2' : '')}`}>
                  <div className="rounded-3xl overflow-hidden shadow-2xl group">
                    <img
                      src={project.image}
                      alt={t(project.titleKey)}
                      className="w-full h-[300px] md:h-[500px] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent"></div>
                    
                    {/* Icon Badge */}
                    <div className={`absolute top-4 md:top-6 ${isRTL ? 'left-4 md:left-6' : 'right-4 md:right-6'} w-12 h-12 md:w-16 md:h-16 animated-gradient-box rounded-2xl flex items-center justify-center shadow-lg`}>
                      <i className={`${project.icon} text-2xl md:text-3xl text-white`}></i>
                    </div>
                  </div>
                  <div className={`absolute -bottom-6 ${index % 2 === 1 ? (isRTL ? '-right-6' : '-left-6') : (isRTL ? '-left-6' : '-right-6')} w-24 h-24 md:w-32 md:h-32 border-2 border-gold/30 rounded-3xl -z-10`}></div>
                </div>

                <div className={`space-y-4 md:space-y-6 ${index % 2 === 1 ? (isRTL ? 'lg:order-2' : 'lg:order-1') : (isRTL ? 'lg:order-1' : '')}`}>
                  <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : ''}`}>
                    {project.tags.map((tagKey, tagIndex) => (
                      <span key={tagIndex} className="px-3 py-1 bg-gold/10 text-gold text-xs font-medium rounded-full">
                        {t(tagKey)}
                      </span>
                    ))}
                  </div>
                  
                  <h2 className="font-serif text-2xl md:text-4xl text-navy">{t(project.titleKey)}</h2>
                  
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                    {t(project.fullDescKey)}
                  </p>

                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="font-semibold text-navy mb-3 text-sm md:text-base">{t('portfolioPage.keyFocus')}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {project.tags.map((tagKey, tagIndex) => (
                        <div key={tagIndex} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <i className="ri-check-line text-gold"></i>
                          <span className="text-gray-600 text-xs md:text-sm">{t(tagKey)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Focus */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('portfolioPage.sectors.label')}</span>
              <div className="w-12 h-px bg-gold"></div>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
              {t('portfolioPage.sectors.title1')} <span className="text-gold">{t('portfolioPage.sectors.title2')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {sectors.map((sector, index) => (
              <div key={index} className="group bg-white rounded-2xl p-6 md:p-8 text-center border border-gray-100 hover:border-gold/30 hover:shadow-xl transition-all duration-500">
                <div className="w-12 h-12 md:w-16 md:h-16 animated-gradient-box rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${sector.icon} text-2xl md:text-3xl text-white`}></i>
                </div>
                <h3 className="font-semibold text-navy text-sm md:text-base">{t(sector.nameKey)}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6">
            {t('portfolioPage.cta.title1')} <span className="text-gold">{t('portfolioPage.cta.title2')}</span>?
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            {t('portfolioPage.cta.description')}
          </p>
          <Link
            to="/contact"
            className={`inline-flex items-center gap-3 px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {t('portfolioPage.cta.button')} <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`}></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
